/**
 * CDD is_pep Yes → PEP 섹션 자동 추가·바인딩.
 * 동일인(신분증번호 또는 이름)이면 중복 행을 만들지 않음.
 */

export type PepPerson = { name: string; idNo: string };

export function normPepName(s: string): string {
  return String(s || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

export function normPepId(s: string): string {
  return String(s || '')
    .trim()
    .toUpperCase()
    .replace(/[\s\-./]/g, '');
}

export function samePepPerson(a: PepPerson, b: PepPerson): boolean {
  const idA = normPepId(a.idNo);
  const idB = normPepId(b.idNo);
  if (idA && idB) return idA === idB;
  const nA = normPepName(a.name);
  const nB = normPepName(b.name);
  if (!nA || !nB) return false;
  return nA === nB;
}

export function isPepYesValue(raw: unknown): boolean {
  const s = String(raw || '')
    .trim()
    .toLowerCase();
  return s === 'yes' || s === 'true' || s === 'y';
}

export type PepSource = {
  source: string;
  name: string;
  idNo: string;
  country: string;
  relationship: string;
};

export type PepSyncLists = {
  directors: number[];
  shareholders: number[];
  ubos: number[];
  peps: number[];
};

export type PepSyncApi = {
  get: (key: string) => string;
  set: (key: string, val: string) => void;
  clearPepId: (id: number) => void;
  setPepIds: (ids: number[]) => void;
  nextPepId: () => number;
  setHasPep: (v: 'yes' | 'no') => void;
};

function fv(api: PepSyncApi, key: string): string {
  return String(api.get(key) || '').trim();
}

export function collectPepYesSources(api: PepSyncApi, lists: PepSyncLists): PepSource[] {
  const out: PepSource[] = [];
  for (const id of lists.directors) {
    if (!isPepYesValue(fv(api, `dir${id}_is_pep`))) continue;
    const name = fv(api, `dir${id}_director_name`);
    if (!name) continue;
    out.push({
      source: `dir:${id}`,
      name,
      idNo: fv(api, `dir${id}_director_identification_no`),
      country: fv(api, `dir${id}_director_nationality`),
      relationship: 'Director',
    });
  }
  for (const id of lists.shareholders) {
    if (!isPepYesValue(fv(api, `sh${id}_is_pep`))) continue;
    const name = fv(api, `sh${id}_shareholder_name`);
    if (!name) continue;
    out.push({
      source: `sh:${id}`,
      name,
      idNo: fv(api, `sh${id}_shareholder_identification_no`),
      country: fv(api, `sh${id}_shareholder_nationality`),
      relationship: 'Shareholder',
    });
  }
  for (const id of lists.ubos) {
    if (!isPepYesValue(fv(api, `ubo${id}_is_pep`))) continue;
    const name = fv(api, `ubo${id}_ubo_name`);
    if (!name) continue;
    out.push({
      source: `ubo:${id}`,
      name,
      idNo: fv(api, `ubo${id}_ubo_identification_no`),
      country: fv(api, `ubo${id}_ubo_nationality`),
      relationship: 'UBO',
    });
  }
  return out;
}

function dedupePepRows(api: PepSyncApi, pepIds: number[]): number[] {
  const keep: number[] = [];
  for (const id of pepIds) {
    const person = {
      name: fv(api, `pep${id}_pep_name`),
      idNo: fv(api, `pep${id}_pep_id_no`),
    };
    if (!person.name) {
      keep.push(id);
      continue;
    }
    const existing = keep.find((kid) =>
      samePepPerson(
        { name: fv(api, `pep${kid}_pep_name`), idNo: fv(api, `pep${kid}_pep_id_no`) },
        person,
      ),
    );
    if (existing != null) {
      const src = fv(api, `pep${id}_pep_source`);
      if (src && src !== 'manual' && !fv(api, `pep${existing}_pep_source`)) {
        api.set(`pep${existing}_pep_source`, src);
      }
      if (fv(api, `pep${id}_pep_country`) && !fv(api, `pep${existing}_pep_country`)) {
        api.set(`pep${existing}_pep_country`, fv(api, `pep${id}_pep_country`));
      }
      if (fv(api, `pep${id}_pep_relationship`) && !fv(api, `pep${existing}_pep_relationship`)) {
        api.set(`pep${existing}_pep_relationship`, fv(api, `pep${id}_pep_relationship`));
      }
      if (fv(api, `pep${id}_pep_id_no`) && !fv(api, `pep${existing}_pep_id_no`)) {
        api.set(`pep${existing}_pep_id_no`, fv(api, `pep${id}_pep_id_no`));
      }
      api.clearPepId(id);
      continue;
    }
    keep.push(id);
  }
  if (!keep.length) return [1];
  const named = keep.filter((id) => fv(api, `pep${id}_pep_name`));
  const empty = keep.filter((id) => !fv(api, `pep${id}_pep_name`));
  const next = named.length ? [...named, ...(empty.length ? [empty[0]] : [])] : [keep[0]];
  for (const id of keep) {
    if (!next.includes(id)) api.clearPepId(id);
  }
  return next;
}

/** is_pep 소스 → PEP 행 동기화. 반환: 최종 pep id 목록 */
export function syncPepFromIsPep(api: PepSyncApi, lists: PepSyncLists): number[] {
  const sources = collectPepYesSources(api, lists);
  const uniqueSources: PepSource[] = [];
  for (const s of sources) {
    if (uniqueSources.some((u) => samePepPerson(u, s))) continue;
    uniqueSources.push(s);
  }

  let pepIds = [...lists.peps];
  if (!pepIds.length) pepIds = [1];
  const used = new Set<number>();

  for (const src of uniqueSources) {
    let pepId =
      pepIds.find((id) => fv(api, `pep${id}_pep_source`) === src.source) ||
      pepIds.find(
        (id) =>
          !used.has(id) &&
          samePepPerson(
            { name: fv(api, `pep${id}_pep_name`), idNo: fv(api, `pep${id}_pep_id_no`) },
            src,
          ),
      ) ||
      pepIds.find(
        (id) => !used.has(id) && !fv(api, `pep${id}_pep_name`) && !fv(api, `pep${id}_pep_source`),
      );

    if (pepId == null) {
      pepId = api.nextPepId();
      pepIds.push(pepId);
    }
    used.add(pepId);
    api.set(`pep${pepId}_pep_source`, src.source);
    api.set(`pep${pepId}_pep_name`, src.name);
    if (src.idNo) api.set(`pep${pepId}_pep_id_no`, src.idNo);
    if (src.country) api.set(`pep${pepId}_pep_country`, src.country);
    const rel = fv(api, `pep${pepId}_pep_relationship`);
    if (!rel || rel === 'Director' || rel === 'Shareholder' || rel === 'UBO') {
      api.set(`pep${pepId}_pep_relationship`, src.relationship);
    }
  }

  const remaining: number[] = [];
  for (const id of pepIds) {
    if (used.has(id)) {
      remaining.push(id);
      continue;
    }
    const source = fv(api, `pep${id}_pep_source`);
    const isAuto = !!source && source !== 'manual';
    if (isAuto) {
      api.clearPepId(id);
      continue;
    }
    if (fv(api, `pep${id}_pep_name`)) {
      remaining.push(id);
      if (!source) api.set(`pep${id}_pep_source`, 'manual');
      continue;
    }
    // 빈 행은 일단 유지 후보 — dedupe 에서 정리
    remaining.push(id);
  }

  const next = dedupePepRows(api, remaining.length ? remaining : [1]);
  api.setPepIds(next);

  const anyNamed = next.some((id) => fv(api, `pep${id}_pep_name`));
  if (uniqueSources.length > 0 || anyNamed) {
    api.setHasPep('yes');
  } else {
    api.setHasPep('no');
  }

  return next;
}
